import { useMemo } from 'react';

import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Control, UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AccountAutoComplete } from '@/components';
import { AccountsCombobox } from '@/components/common/accounts-combobox';
import { AdvancedDonorLookup } from '@/components/common/advanced-donor-lookup';
import { AdvancedSireLookup } from '@/components/common/advanced-sire-lookup';
import { BreedsAutoComplete } from '@/components/common/breeds-auto-complete';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FormField } from '@/pages/embryo-details/components/FormField';
import { EmbryoDetailDto } from '@/services/embryo-service';

import {
  BULK_LOCKED_FIELDS,
  LOCKED_IDENTIFIER_FIELDS,
  OUTSIDE_EMBRYO_EDITABLE_FIELDS,
  OUTSIDE_EMBRYO_GENERAL_FIELDS,
} from './constants';
import { EmbryoFormFieldConfig } from './field-config';
import {
  embryoStatusOptions,
  embryoStatusOptionsWithMissing,
  freshFrozenOptions,
  sexOptions,
} from './form-options';
import { useEmbryoFieldPolicy, useEmbryoFieldsEnabled } from './full-inventory';

type EmbryoGeneralTabProps = {
  control: Control<EmbryoDetailDto>;
  watch: UseFormWatch<EmbryoDetailDto>;
  isBulkMode: boolean;
  hasManagerWrite: boolean;
  columns?: number;
  isOutsideEmbryo?: boolean;
};

type BreedLookupValue = {
  id: string;
  code?: string | null;
};

export function EmbryoGeneralTab({
  control,
  watch,
  isBulkMode,
  hasManagerWrite,
  columns = 3,
  isOutsideEmbryo = false,
}: EmbryoGeneralTabProps) {
  const { t } = useTranslation();
  const addRemoveEmbryoFields = useEmbryoFieldsEnabled();
  const isAccountComboboxEnabled = useFeatureFlag(
    'Account_Combobox_not_Dropdown',
  );
  const policy = useEmbryoFieldPolicy({ isOutsideEmbryo });
  const embryoMissingStatus = useFeatureFlag('Embryo_Missing_Status');
  const bulkLockedFieldsEnabled = useFeatureFlag(
    'Embryo_Bulk_Edit_Locked_Fields',
  );
  const bulkOwnerOptional =
    useFeatureFlag('Embryo_Bulk_Owner_Optional') && isBulkMode;
  // Outside embryos are exempt: their bulk edit exists precisely to set
  // donor/sire matings, so the wider lock list would break it.
  const bulkLockedFields: readonly string[] =
    bulkLockedFieldsEnabled && !isOutsideEmbryo
      ? BULK_LOCKED_FIELDS
      : LOCKED_IDENTIFIER_FIELDS;

  const breedId = watch('breedId');
  const breedName = watch('breedName');
  const breedDefaultValue = useMemo(() => {
    if (isBulkMode || !breedId) return undefined;
    if (typeof breedId === 'object' && breedId !== null && 'id' in breedId) {
      const breedValue = breedId as BreedLookupValue;
      return { ...breedValue, code: breedValue.code ?? '' };
    }
    return { id: breedId, name: breedName ?? '', code: '' };
  }, [isBulkMode, breedId, breedName]);

  const ownerId = watch('ownerId');
  const ownerName = watch('ownerName');
  const ownerDefaultValue = useMemo(() => {
    if (isBulkMode || !ownerId) return undefined;
    return { id: ownerId, name: ownerName ?? '', code: '' };
  }, [isBulkMode, ownerId, ownerName]);

  const billingOwnerId = watch('billingOwnerId');
  const billingOwnerName = watch('billingOwnerName');
  const billingOwnerDefaultValue = useMemo(() => {
    if (isBulkMode || !billingOwnerId) return undefined;
    return { id: billingOwnerId, name: billingOwnerName ?? '', code: '' };
  }, [isBulkMode, billingOwnerId, billingOwnerName]);

  const transferOwnerId = watch('transferOwnerId');
  const transferOwnerName = watch('transferOwnerName');
  const transferOwnerDefaultValue = useMemo(() => {
    if (isBulkMode || !transferOwnerId) return undefined;
    return { id: transferOwnerId, name: transferOwnerName ?? '', code: '' };
  }, [isBulkMode, transferOwnerId, transferOwnerName]);

  const fieldConfigs: EmbryoFormFieldConfig[] = useMemo(
    () => [
      {
        name: 'name',
        label: t('embryo.embryoName'),
        component: InputText,
        requiresManagerWrite: true,
        ...(addRemoveEmbryoFields && { required: true }),
      },
      {
        name: 'status',
        label: t('embryo.embryoStatus'),
        component: Dropdown,
        componentProps: {
          options: embryoMissingStatus
            ? embryoStatusOptionsWithMissing
            : embryoStatusOptions,
          placeholder: 'Select status',
        },
      },
      {
        name: 'freshFrozen',
        label: t('embryo.freshFrozen'),
        component: Dropdown,
        ...(addRemoveEmbryoFields && { required: true }),
        componentProps: {
          options: freshFrozenOptions,
          placeholder: t('common.selectType'),
        },
      },
      {
        name: 'donor',
        label: t('embryo.donor'),
        component: AdvancedDonorLookup,
        requiresManagerWrite: true,
        ...(addRemoveEmbryoFields && { required: true }),
      },
      {
        name: 'sire',
        label: t('embryo.sire'),
        component: AdvancedSireLookup,
        requiresManagerWrite: true,
      },
      {
        name: 'ownerId',
        label: t('embryo.embryoOwner'),
        ...(!isAccountComboboxEnabled && {}),
        component: isAccountComboboxEnabled
          ? AccountsCombobox
          : AccountAutoComplete,
        ...(addRemoveEmbryoFields && { required: true }),
        componentProps: isAccountComboboxEnabled
          ? {}
          : {
              placeholder: t('common.selectOption'),
              defaultValue: ownerDefaultValue,
              appendTo: null,
            },
      },
      {
        name: 'transferOwnerId',
        label: t('embryo.embryoOwner'),
        ...(!isAccountComboboxEnabled && {}),
        component: isAccountComboboxEnabled
          ? AccountsCombobox
          : AccountAutoComplete,
        outsideOnly: true,
        componentProps: isAccountComboboxEnabled
          ? {}
          : {
              placeholder: t('common.selectOption'),
              defaultValue: transferOwnerDefaultValue,
              appendTo: null,
            },
      },
      {
        name: 'billingOwnerId',
        label: t('embryo.billingOwner'),
        ...(!isAccountComboboxEnabled && {}),
        component: isAccountComboboxEnabled
          ? AccountsCombobox
          : AccountAutoComplete,
        ...(addRemoveEmbryoFields && { required: true }),
        componentProps: isAccountComboboxEnabled
          ? {}
          : {
              placeholder: t('common.selectOption'),
              defaultValue: billingOwnerDefaultValue,
              appendTo: null,
            },
        requiresManagerWrite: true,
      },
      {
        name: 'soldTo',
        label: t('embryo.soldTo'),
        component: InputText,
      },
      {
        name: 'strawDisclaimer',
        label: t('straw.strawDisclaimer'),
        component: InputText,
      },
      {
        name: 'embTagNumber',
        label: t('embryo.embryoTag'),
        component: InputNumber,
      },
      {
        name: 'strawNumber',
        label: t('straw.straw'),
        component: InputNumber,
      },
      {
        name: 'sex',
        label: t('embryo.sex'),
        component: Dropdown,
        componentProps: {
          options: sexOptions,
          placeholder: t('common.select'),
        },
      },
      {
        name: 'breedId',
        label: t('embryo.breed'),
        component: BreedsAutoComplete,
        componentProps: {
          defaultValue: breedDefaultValue,
        },
        requiresManagerWrite: true,
      },
      {
        name: 'freshStrawNo',
        label: t('embryo.freshStrawNumber'),
        component: InputNumber,
      },
      {
        name: 'programName',
        label: t('embryo.programName'),
        component: InputText,
      },
      ...(addRemoveEmbryoFields
        ? ([
            {
              name: 'donorReg',
              label: t('embryo.regNumber'),
              component: InputText,
            },
            {
              name: 'donorBreedId',
              label: t('embryo.donorBreed'),
              component: BreedsAutoComplete,
            },
            {
              name: 'naabCode',
              label: t('embryo.sireNaab'),
              component: InputText,
            },
            {
              name: 'sireBreedId',
              label: t('embryo.sireBreed'),
              component: BreedsAutoComplete,
            },
            {
              name: 'semenColCode',
              label: t('embryo.semenCollectionCode'),
              component: InputText,
            },
            {
              name: 'ownerCode',
              label: t('embryo.ranchId'),
              component: InputText,
              disabled: true,
            },
            {
              name: 'edonorId',
              label: t('embryo.edonorId'),
              component: InputText,
            },
            {
              name: 'embryoPa',
              label: t('embryo.embryoPa'),
              component: InputNumber,
            },
          ] satisfies EmbryoFormFieldConfig[])
        : []),
    ],
    [
      t,
      addRemoveEmbryoFields,
      embryoMissingStatus,
      breedDefaultValue,
      isAccountComboboxEnabled,
      ownerDefaultValue,
      billingOwnerDefaultValue,
      transferOwnerDefaultValue,
    ],
  );

  const isFieldLocked = (field: EmbryoFormFieldConfig) =>
    (field.requiresManagerWrite === true && !hasManagerWrite) ||
    // Bulk edits lock parentage/ownership as well as identity (WI 37965).
    (isBulkMode && bulkLockedFields.includes(field.name)) ||
    (isOutsideEmbryo &&
      !(OUTSIDE_EMBRYO_EDITABLE_FIELDS as readonly string[]).includes(
        field.name,
      ));

  const visibleFields = (
    isOutsideEmbryo
      ? fieldConfigs.filter((f) =>
          (OUTSIDE_EMBRYO_GENERAL_FIELDS as readonly string[]).includes(f.name),
        )
      : fieldConfigs.filter((f) => !f.outsideOnly)
  ).filter((f) => policy.isVisible(f.name));

  return (
    <div className="general-tab">
      <div
        className="grid gap-x-6 gap-y-1"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {visibleFields.map((field) => (
          <FormField
            key={field.name}
            label={field.label}
            name={field.name}
            control={control}
            component={field.component}
            disabled={
              field.disabled ||
              isFieldLocked(field) ||
              policy.isLocked(field.name)
            }
            required={
              !(bulkOwnerOptional && field.name === 'ownerId') &&
              policy.isRequired(field.name, field.required)
            }
            componentProps={field.componentProps}
            lockedDisplayField={field.lockedDisplayField}
          />
        ))}
      </div>
    </div>
  );
}
